import {
  CALL_PHASES,
  PARTICIPANT_ROSTER_ACTION_TYPES,
  createParticipantRosterState,
  getParticipantRosterParticipant,
  getParticipantRosterParticipants,
  getSurfacePolicy,
  getVisibleParticipantsForSurface,
  normalizeCallPhase,
  normalizeSessionInterfaceId,
  normalizeSurfacePolicyId,
  pickDominantParticipantForSurface,
  reduceParticipantRoster,
  resolveParticipantId,
} from '../domain'
import {
  PRESENCE_EVENT_TYPES,
  RTC_SESSION_EVENT_TYPES,
  assertPresencePort,
  assertRtcSessionPort,
  assertVideoBinderPort,
} from '../ports'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function hasOwn(objectValue, propertyName) {
  return Object.prototype.hasOwnProperty.call(objectValue, propertyName)
}

function mapTransportStateToCallPhase(
  transportState,
  fallbackValue = CALL_PHASES.idle,
) {
  const normalizedTransportState =
    typeof transportState === 'string' ? transportState.trim().toLowerCase() : ''

  switch (normalizedTransportState) {
    case 'preview':
      return CALL_PHASES.idle
    case 'connecting':
    case 'joining':
      return CALL_PHASES.joining
    case 'connected':
    case 'joined':
      return CALL_PHASES.joined
    case 'reconnecting':
      return CALL_PHASES.reconnecting
    case 'disconnecting':
    case 'left':
    case 'disconnected':
      return CALL_PHASES.left
    case 'failed':
    case 'error':
      return CALL_PHASES.failed
    default:
      return fallbackValue
  }
}

function normalizeParticipantMetadata(participantLike = {}) {
  const participantId = resolveParticipantId(participantLike)

  if (!participantId) {
    return null
  }

  const metadata = {
    participantId,
    userId: normalizeOptionalString(participantLike?.userId),
    actorType: normalizeOptionalString(participantLike?.actorType),
    actorId: normalizeOptionalString(
      participantLike?.actorId ?? participantLike?.stableActorId,
    ),
    displayName: normalizeOptionalString(participantLike?.displayName),
    profilePicId: normalizeOptionalString(
      participantLike?.profilePicId ?? participantLike?.avatarImageId,
    ),
    role: normalizeOptionalString(participantLike?.role),
  }

  if (typeof participantLike?.isLocal === 'boolean') {
    metadata.isLocal = participantLike.isLocal
  }

  return metadata
}

function mergeMetadata(existingMetadata, nextMetadata) {
  return {
    ...existingMetadata,
    ...nextMetadata,
    participantId:
      nextMetadata?.participantId || existingMetadata?.participantId || null,
    userId: nextMetadata?.userId || existingMetadata?.userId || null,
    actorType: nextMetadata?.actorType || existingMetadata?.actorType || null,
    actorId: nextMetadata?.actorId || existingMetadata?.actorId || null,
    displayName:
      nextMetadata?.displayName || existingMetadata?.displayName || null,
    profilePicId:
      nextMetadata?.profilePicId || existingMetadata?.profilePicId || null,
    role: nextMetadata?.role || existingMetadata?.role || null,
    isLocal:
      typeof nextMetadata?.isLocal === 'boolean'
        ? nextMetadata.isLocal
        : existingMetadata?.isLocal,
  }
}

function buildParticipantFromSources(participantLike, metadataById) {
  const participantId = resolveParticipantId(participantLike)
  const participantMetadata = participantId ? metadataById[participantId] : null
  const nextParticipant = isPlainObject(participantLike) ? participantLike : {}

  return {
    ...participantMetadata,
    ...nextParticipant,
    participantId:
      participantId || participantMetadata?.participantId || nextParticipant?.id,
    userId: nextParticipant?.userId ?? participantMetadata?.userId,
    actorType: nextParticipant?.actorType ?? participantMetadata?.actorType,
    actorId:
      nextParticipant?.actorId ??
      nextParticipant?.stableActorId ??
      participantMetadata?.actorId,
    displayName:
      nextParticipant?.displayName ?? participantMetadata?.displayName ?? '',
    profilePicId:
      nextParticipant?.profilePicId ??
      nextParticipant?.avatarImageId ??
      participantMetadata?.profilePicId ??
      null,
    role: nextParticipant?.role ?? participantMetadata?.role ?? null,
    isLocal:
      typeof nextParticipant?.isLocal === 'boolean'
        ? nextParticipant.isLocal
        : Boolean(participantMetadata?.isLocal),
  }
}

/**
 * Provider-neutral session controller that converts provider port events into
 * domain state for the shared presentation shell.
 *
 * @param {object} [options={}]
 * @param {import('../ports').RtcSessionPort} [options.rtcSession]
 * @param {import('../ports').PresencePort} [options.presence]
 * @param {import('../ports').VideoBinderPort} [options.videoBinder]
 * @param {string} [options.callPhase]
 * @param {string} [options.sessionInterfaceId]
 * @param {string} [options.surfacePolicyId]
 * @param {string} [options.preferredDominantParticipantId]
 * @param {object} [options.localParticipantIdentity]
 * @param {object[]} [options.initialParticipants]
 * @returns {object}
 */
export function createLiveSessionController(options = {}) {
  const rtcSession = options?.rtcSession
    ? assertRtcSessionPort(options.rtcSession)
    : null
  const presence = options?.presence
    ? assertPresencePort(options.presence)
    : null
  const videoBinder = options?.videoBinder
    ? assertVideoBinderPort(options.videoBinder)
    : null
  const listeners = new Set()
  const disposers = []

  let state = {
    callPhase: normalizeCallPhase(options?.callPhase, CALL_PHASES.idle),
    sessionInterfaceId: normalizeSessionInterfaceId(
      options?.sessionInterfaceId,
    ),
    surfacePolicyId: normalizeSurfacePolicyId(
      options?.surfacePolicyId ?? options?.sessionInterfaceId,
    ),
    preferredDominantParticipantId: normalizeOptionalString(
      options?.preferredDominantParticipantId,
    ),
    localParticipantId: null,
    roster: createParticipantRosterState(options?.initialParticipants || []),
    participantMetadataById: {},
    lastError: null,
  }

  function getSnapshot() {
    const participants = getParticipantRosterParticipants(state.roster)
    const visibleParticipants = getVisibleParticipantsForSurface(
      state.surfacePolicyId,
      participants,
    )

    return {
      ...state,
      surfacePolicy: getSurfacePolicy(state.surfacePolicyId),
      participants,
      visibleParticipants,
      dominantParticipantId:
        pickDominantParticipantForSurface(
          state.surfacePolicyId,
          visibleParticipants,
          state.preferredDominantParticipantId,
        )?.id || null,
      videoBinder,
    }
  }

  function emit() {
    const snapshot = getSnapshot()

    listeners.forEach((listener) => {
      listener(snapshot)
    })
  }

  function persistParticipantMetadata(participantLike = {}) {
    const normalizedMetadata = normalizeParticipantMetadata(participantLike)

    if (!normalizedMetadata) {
      return null
    }

    const previousMetadata =
      state.participantMetadataById[normalizedMetadata.participantId] || null

    state = {
      ...state,
      participantMetadataById: {
        ...state.participantMetadataById,
        [normalizedMetadata.participantId]: mergeMetadata(
          previousMetadata,
          normalizedMetadata,
        ),
      },
    }

    return state.participantMetadataById[normalizedMetadata.participantId]
  }

  function upsertParticipant(participantLike = {}) {
    const participantInput = buildParticipantFromSources(
      participantLike,
      state.participantMetadataById,
    )

    state = {
      ...state,
      roster: reduceParticipantRoster(state.roster, {
        type: PARTICIPANT_ROSTER_ACTION_TYPES.participantUpserted,
        participant: participantInput,
      }),
    }
  }

  function replaceRosterParticipants(participants = []) {
    const nextParticipants = [...participants]
    const localParticipant =
      state.localParticipantId &&
      getParticipantRosterParticipant(state.roster, state.localParticipantId)

    if (
      localParticipant &&
      !nextParticipants.some(
        (participant) =>
          resolveParticipantId(participant) === state.localParticipantId,
      )
    ) {
      nextParticipants.push(localParticipant)
    }

    state = {
      ...state,
      roster: reduceParticipantRoster(state.roster, {
        type: PARTICIPANT_ROSTER_ACTION_TYPES.rosterReplaced,
        participants: nextParticipants.map((participant) =>
          buildParticipantFromSources(participant, state.participantMetadataById),
        ),
      }),
    }
  }

  function setCallPhase(callPhase) {
    state = {
      ...state,
      callPhase: normalizeCallPhase(callPhase, state.callPhase),
    }
    emit()
  }

  function setPreferredDominantParticipantId(participantId) {
    state = {
      ...state,
      preferredDominantParticipantId: normalizeOptionalString(participantId),
    }
    emit()
  }

  function setSurfacePolicyId(surfacePolicyId) {
    state = {
      ...state,
      surfacePolicyId: normalizeSurfacePolicyId(
        surfacePolicyId,
        state.surfacePolicyId,
      ),
    }
    emit()
  }

  function setLocalParticipantIdentity(localParticipantIdentity = {}) {
    const normalizedMetadata = persistParticipantMetadata({
      ...localParticipantIdentity,
      isLocal: true,
    })

    if (!normalizedMetadata?.participantId) {
      return null
    }

    state = {
      ...state,
      localParticipantId: normalizedMetadata.participantId,
    }

    upsertParticipant({
      ...localParticipantIdentity,
      participantId: normalizedMetadata.participantId,
      isLocal: true,
      connectionState:
        state.callPhase === CALL_PHASES.joined
          ? 'connected'
          : localParticipantIdentity?.connectionState || 'joining',
    })
    emit()
    return normalizedMetadata.participantId
  }

  function applyRtcEvent(event = {}) {
    switch (event?.type) {
      case RTC_SESSION_EVENT_TYPES.participantsChanged: {
        if (Array.isArray(event?.participants)) {
          event.participants.forEach((participant) =>
            persistParticipantMetadata(participant),
          )
          replaceRosterParticipants(event.participants)
        } else if (event?.changeType === 'left') {
          state = {
            ...state,
            roster: reduceParticipantRoster(state.roster, {
              type: PARTICIPANT_ROSTER_ACTION_TYPES.participantLeft,
              participant: event?.participant,
              transport: event?.transport,
            }),
          }
        } else if (event?.changeType === 'media') {
          state = {
            ...state,
            roster: reduceParticipantRoster(state.roster, {
              type: PARTICIPANT_ROSTER_ACTION_TYPES.participantMediaChanged,
              participant: buildParticipantFromSources(
                event?.participant || {},
                state.participantMetadataById,
              ),
              audioMuted: event?.audioMuted,
              videoMuted: event?.videoMuted,
              screenSharing: event?.screenSharing,
              videoHandle: hasOwn(event, 'videoHandle') ? event.videoHandle : undefined,
              transport: event?.transport,
            }),
          }
        } else if (event?.participant) {
          persistParticipantMetadata(event.participant)
          upsertParticipant(event.participant)
        }

        emit()
        return
      }
      case RTC_SESSION_EVENT_TYPES.stateChanged:
        state = {
          ...state,
          callPhase: normalizeCallPhase(
            event?.callPhase,
            mapTransportStateToCallPhase(
              event?.sessionState,
              state.callPhase,
            ),
          ),
        }
        emit()
        return
      case RTC_SESSION_EVENT_TYPES.error:
        state = {
          ...state,
          lastError: event?.error || event,
          callPhase: CALL_PHASES.failed,
        }
        emit()
        return
      default:
        return
    }
  }

  function applyPresenceEvent(event = {}) {
    switch (event?.type) {
      case PRESENCE_EVENT_TYPES.participantMetadataChanged:
      case PRESENCE_EVENT_TYPES.participantJoined: {
        const metadata =
          persistParticipantMetadata(event?.participant || event?.metadata || event) ||
          null
        const rosterParticipant = metadata?.participantId
          ? getParticipantRosterParticipant(state.roster, metadata.participantId)
          : null

        if (
          metadata?.participantId &&
          (event?.admitToRoster || rosterParticipant)
        ) {
          upsertParticipant(
            buildParticipantFromSources(
              event?.participant || event?.metadata || metadata,
              state.participantMetadataById,
            ),
          )
        }

        emit()
        return
      }
      case PRESENCE_EVENT_TYPES.participantLeft:
        state = {
          ...state,
          roster: reduceParticipantRoster(state.roster, {
            type: PARTICIPANT_ROSTER_ACTION_TYPES.participantLeft,
            participant: event?.participant,
            transport: event?.transport,
          }),
        }
        emit()
        return
      case PRESENCE_EVENT_TYPES.error:
        state = {
          ...state,
          lastError: event?.error || event,
        }
        emit()
        return
      default:
        return
    }
  }

  if (rtcSession?.subscribe) {
    disposers.push(rtcSession.subscribe(applyRtcEvent))
  }

  if (presence?.subscribe) {
    disposers.push(presence.subscribe(applyPresenceEvent))
  }

  if (options?.localParticipantIdentity) {
    setLocalParticipantIdentity(options.localParticipantIdentity)
  }

  return {
    getState: getSnapshot,
    subscribe(listener) {
      listeners.add(listener)
      listener(getSnapshot())

      return () => {
        listeners.delete(listener)
      }
    },
    setCallPhase,
    setSurfacePolicyId,
    setPreferredDominantParticipantId,
    setLocalParticipantIdentity,
    replaceParticipants(participants = []) {
      participants.forEach((participant) => persistParticipantMetadata(participant))
      replaceRosterParticipants(participants)
      emit()
    },
    applyRtcEvent,
    applyPresenceEvent,
    destroy() {
      while (disposers.length) {
        const dispose = disposers.pop()

        if (typeof dispose === 'function') {
          dispose()
        }
      }

      listeners.clear()
    },
  }
}
